import {defineStore} from 'pinia'
import Srv from '@/services/GraphQLSrv'
import type {RouteLocationNormalizedLoaded} from 'vue-router'
import type {CatalogMenuComponent, CatalogMenuGroup, CatalogMenuItem, View} from '@/types/View'
import {appViewPremissions} from '@/composables/roles'
import {kebabToCamel} from '@/helpers/tools'
import {useAuthStore} from './auth'

export const useViewsStore = defineStore('views', {
  state: () => ({
    viewsLoading: false,
    views: [] as View[],
    viewsList: [] as View[],
    viewsListPagination: {
      currentPage: 1,
      perPage: 20,
      total: 0,
      hasMorePages: false,
      lastPage: 1,
    },
  }),
  getters: {
    viewNames: state => [...new Set(state.views.map(v => v.meta.viewName))],
    getCatalogMenu: state => {
      const authStore = useAuthStore()
      const component = state.views.find(view => view.meta.viewName === 'catalogmenu')

      if (!component) return []

      const views = state.views
        .filter(v => !v.meta.viewType)
        .map(v => [v.meta.viewName, v.meta.permission] as [string, string])

      const viewsMap = new Map<string, string>([...appViewPremissions, ...views])

      const menuComp = component.components.find(c => c.componentType === 'catalogmenu') as CatalogMenuComponent | undefined
      if (!menuComp) return []
      const rows = menuComp.componentParams.appearance.rows

      const filterByPermission = (item: CatalogMenuItem): CatalogMenuItem | null => {
        // Проверяем, является ли элемент группой
        if ('type' in item && item.type === 'group') {
          const group = item as CatalogMenuGroup
          // Фильтруем элементы внутри группы
          const filteredItems = group.items
            .map(subItem => {
              const permission = viewsMap.get(subItem.route)
              // Allow-by-default: если у пункта не задан permission (например, view без
              // meta.permission, либо роут не прописан в appViewPremissions) — показываем.
              // Для L2-пользователей catalogmenu вообще не используется, они видят
              // хардкод-меню L2_MENU_GROUP из VAsideMenu.vue.
              if (!permission) return subItem
              if (!authStore.userPermissions.has(permission)) return null
              return subItem
            })
            .filter((subItem): subItem is CatalogMenuRow => subItem !== null)

          // Скрываем группу, если после фильтра внутри не осталось доступных пунктов
          if (filteredItems.length === 0) return null

          return {
            ...group,
            items: filteredItems
          } as CatalogMenuGroup
        }

        // Обычный элемент меню — allow-by-default по тому же принципу
        const permission = viewsMap.get(item.route)
        if (!permission) return item
        if (!authStore.userPermissions.has(permission)) return null
        return item
      }

      return rows.map(filterByPermission).filter((r): r is CatalogMenuItem => r !== null)
    },
    getView: state => (viewName: string, type?: string) => {
      const authStore = useAuthStore()
      const normalizedView = kebabToCamel(viewName)
      const normalizedType = type || undefined
      const view = state.views.find(view => view.meta.viewName === normalizedView && (view.meta.viewType || undefined) === normalizedType)!

      if (!view?.meta) {
        return {meta: {}} as View
      }
      if (!view.meta.permission) {
        return view
      }
      if (authStore.userPermissions.has(view.meta.permission)) {
        return view
      }
      // Если у пользователя нет permission, возвращаем undefined
      return undefined
    },
    hasCreateView: state => (viewName: string) => {
      const authStore = useAuthStore()
      const normalizedView = kebabToCamel(viewName)
      const view = state.views.find(view => view.meta.viewName === normalizedView && (view.meta.viewType || undefined) === 'create')

      if (!view) {
        return false
      }

      if (!view.meta.permission) {
        return true
      }

      return authStore.checkPermission(view.meta)
    },
  },
  actions: {
    async reqViews() {
      this.viewsLoading = true

      try {
        const {list} = await Srv.query('views', ['uuid', 'data'], true, {perPage: 5000})

        this.views = list.map(({uuid, data}) => {
          const json = typeof data === 'string' ? JSON.parse(data) : data

          if ('meta' in json) {
            json.meta = {...json.meta, uuid}
            return json
          }

          return {
            meta: {uuid, viewName: 'unknown'},
            components: [{
              componentType: 'table',
              componentParams: {
                appearance: {columns: []},
                dataSource: {method: ''},
                filters: [],
              },
            }],
          }
        })
      } catch {}

      this.viewsLoading = false
    },
    // Гидрация из батч-запроса (принимает сырые данные без HTTP запроса)
    hydrateViews(rawList: any[]) {
      this.viewsLoading = true
      try {
        this.views = rawList.map(({uuid, data}) => {
          const json = typeof data === 'string' ? JSON.parse(data) : data

          if ('meta' in json) {
            json.meta = {...json.meta, uuid}
            return json
          }

          return {
            meta: {uuid, viewName: 'unknown'},
            components: [{
              componentType: 'table',
              componentParams: {
                appearance: {columns: []},
                dataSource: {method: ''},
                filters: [],
              },
            }],
          }
        })
      } catch (err) {
        console.warn('[views] hydrateViews parse error — views left empty:', err)
      }
      this.viewsLoading = false
    },
    async reqViewsList(page = 1, perPage = 20, filters?: Record<string, {operator: string, value: string}>) {
      this.viewsLoading = true

      try {
        const params: Record<string, any> = {page, perPage}
        if (filters) {
          params.filters = filters
        }

        const res = await Srv.query('views', ['uuid', 'data'], true, params)

        this.viewsList = res.list.map(({uuid, data}) => {
          const json = typeof data === 'string' ? JSON.parse(data) : data

          if ('meta' in json) {
            json.meta = {...json.meta, uuid}
            return json
          }

          return {
            meta: {uuid, viewName: 'unknown'},
            components: [{
              componentType: 'table',
              componentParams: {
                appearance: {columns: []},
                dataSource: {method: ''},
                filters: [],
              },
            }],
          }
        })

        const {data, ...pagination} = res.data
        this.viewsListPagination = {...pagination}
      } catch {}

      this.viewsLoading = false
    },
  },
})

export function checkIsView(route: RouteLocationNormalizedLoaded) {
  return route?.path.includes('/views/')
}
